import { Euler, Matrix4, Quaternion, Spherical, Vector3 } from 'three';
import { DESTINATIONS, HOME, SOLIDS, type Destination } from './catalog';
import { STRUCTURE_OBSTACLES, structureSpeedLimit, sweepStructures } from './structures';

export type FlightMode = 'orbit' | 'free' | 'cruise';
export interface FlightInput { forward: number; right: number; up: number; boost: boolean; brake: boolean }
export const NO_INPUT: FlightInput = { forward: 0, right: 0, up: 0, boost: false, brake: false };
const UP = new Vector3(0, 1, 0);
const matrix = new Matrix4();
const navigationObstacles = [...SOLIDS, ...STRUCTURE_OBSTACLES];
// Frame the ring station within the forward desktop windscreen.
const OPENING_LOOK = { wide: new Vector3(14, -18, -8), compact: new Vector3(0, 4, 0) };

/** Swept sphere collision: a fast frame cannot tunnel through a planet. */
export function safePosition(from: Vector3, proposed: Vector3): Vector3 {
  const result = proposed.clone();
  for (const solid of SOLIDS) {
    const center = new Vector3(...solid.position), offset = from.clone().sub(center);
    const delta = result.clone().sub(from), radius = solid.radius + .6;
    if (offset.length() < radius) {
      result.copy(center).add(offset.lengthSq() > .0001 ? offset.setLength(radius + .1) : new Vector3(0, radius + .1, 0));
      continue;
    }
    const a = delta.lengthSq(), b = 2 * offset.dot(delta), c = offset.lengthSq() - radius * radius;
    const d = b * b - 4 * a * c;
    if (a > 0 && d >= 0) {
      const t = (-b - Math.sqrt(d)) / (2 * a);
      if (t >= 0 && t <= 1) result.copy(from).addScaledVector(delta, Math.max(0, t - .002));
    }
  }
  return sweepStructures(from, result);
}

export class Flight {
  readonly position = new Vector3(...HOME.view).add(new Vector3(...HOME.position));
  readonly rotation = new Quaternion();
  readonly velocity = new Vector3();
  mode: FlightMode = 'orbit';
  target = HOME;
  speedLevel = 3;
  traveled = 0;
  private orbitAngle = new Spherical().setFromVector3(new Vector3(...HOME.view));
  private orbitGoal = this.orbitAngle.clone();
  private orbitZoom = 1;
  private zoomTarget = 1;
  private openingFrame = true;
  private arrival = false;

  constructor(public compact = false) { this.face(compact ? OPENING_LOOK.compact : OPENING_LOOK.wide); }
  get speed() { return this.velocity.length(); }
  get arrived() { return this.arrival; }
  get nearest() {
    return DESTINATIONS.reduce((a, b) => this.position.distanceTo(new Vector3(...a.position)) - a.radius < this.position.distanceTo(new Vector3(...b.position)) - b.radius ? a : b);
  }
  private face(point: Vector3) { this.rotation.setFromRotationMatrix(matrix.lookAt(this.position, point, UP)); }
  private aim(point: Vector3, dt: number, rate = 2) {
    const q = new Quaternion().setFromRotationMatrix(matrix.lookAt(this.position, point, UP));
    this.rotation.slerp(q, 1 - Math.exp(-rate * dt));
  }
  free() { this.mode = 'free'; this.arrival = false; }
  navigate(destination: Destination) {
    this.target = destination; this.mode = 'cruise'; this.arrival = false;
    // Selection never changes position. The same velocity integrator flies the entire route.
  }
  orbit(destination: Destination) {
    const offset = this.position.clone().sub(new Vector3(...destination.position));
    const solid = destination.kind !== 'nebula' && destination.kind !== 'galaxy';
    if ((solid && offset.length() < destination.radius * 2.4) || offset.length() > Math.max(destination.radius * 8, 380)) { this.navigate(destination); return; }
    this.target = destination; this.mode = 'orbit'; this.velocity.set(0, 0, 0);
    this.orbitAngle.setFromVector3(offset); this.orbitGoal.copy(this.orbitAngle);
    this.orbitZoom = this.zoomTarget = 1; this.openingFrame = false;
  }
  look(dx: number, dy: number) {
    if (this.mode === 'orbit') {
      // Drag the world around its centre, without abandoning the orbit or teleporting.
      this.orbitGoal.theta -= dx;
      this.orbitGoal.phi = Math.max(.08, Math.min(Math.PI - .08, this.orbitGoal.phi - dy));
      this.openingFrame = false;
      return;
    }
    if (this.mode !== 'free') this.free();
    // A stable up direction and bounded pitch prevent an accidental upside-down view.
    const angles = new Euler().setFromQuaternion(this.rotation, 'YXZ');
    angles.y -= dx;
    angles.x = Math.max(-Math.PI * .475, Math.min(Math.PI * .475, angles.x - dy));
    angles.z = 0;
    this.rotation.setFromEuler(angles);
  }
  zoom(delta: number) {
    if (this.mode === 'orbit') this.zoomTarget = Math.max(.48, Math.min(2.8, this.zoomTarget * Math.exp(delta * .001)));
    else this.speedLevel = Math.max(1, Math.min(6, this.speedLevel - delta / 360));
  }
  stop() { this.free(); this.velocity.set(0, 0, 0); }

  update(dt: number, input: FlightInput = NO_INPUT, reducedMotion = false) {
    dt = Math.max(0, Math.min(.1, dt));
    if (!dt) return;
    if (input.forward || input.right || input.up || input.brake) this.free();
    const old = this.position.clone();
    if (this.mode === 'orbit') {
      const center = new Vector3(...this.target.position);
      if (!reducedMotion) { const drift = (this.openingFrame ? .003 : .013) * dt; this.orbitAngle.theta += drift; this.orbitGoal.theta += drift; }
      const blend = 1 - Math.exp(-14 * dt);
      this.orbitAngle.theta += (this.orbitGoal.theta - this.orbitAngle.theta) * blend;
      this.orbitAngle.phi += (this.orbitGoal.phi - this.orbitAngle.phi) * blend;
      this.orbitZoom += (this.zoomTarget - this.orbitZoom) * (1 - Math.exp(-7 * dt));
      const offset = new Vector3().setFromSpherical(this.orbitAngle).multiplyScalar(this.orbitZoom);
      this.position.copy(safePosition(old, center.clone().add(offset)));
      const bias = this.openingFrame && this.target.id === HOME.id ? (this.compact ? OPENING_LOOK.compact : OPENING_LOOK.wide) : new Vector3();
      this.aim(center.add(bias), dt, this.openingFrame ? 2 : 18);
      this.velocity.set(0, 0, 0);
    } else if (this.mode === 'cruise') {
      const endpoint = new Vector3(...this.target.position).add(new Vector3(...this.target.view));
      const distance = this.position.distanceTo(endpoint);
      if (distance < 2 && this.speed < 12) {
        this.orbit(this.target); this.arrival = true;
      } else {
        let waypoint = endpoint.clone();
        const direction = endpoint.clone().sub(this.position).normalize();
        // Steer around intervening worlds instead of clipping through them.
        for (const solid of navigationObstacles) {
          const center = new Vector3(...solid.position), relative = center.clone().sub(this.position);
          const along = relative.dot(direction), clearance = solid.radius * 1.65 + 10;
          if (along > 0 && along < distance && relative.clone().addScaledVector(direction, -along).length() < clearance && along < Math.max(500, this.speed * 2.5)) {
            let side = this.position.clone().sub(center).addScaledVector(direction, along);
            if (side.length() < 1) side = new Vector3().crossVectors(direction, UP);
            if (side.length() < .01) side.set(1, 0, 0);
            waypoint = center.add(side.setLength(clearance * 1.4)); break;
          }
        }
        const desiredSpeed = Math.min(880, Math.sqrt(distance * 190), distance * 1.5);
        const desired = waypoint.clone().sub(this.position).normalize().multiplyScalar(desiredSpeed);
        const change = desired.sub(this.velocity).clampLength(0, 150 * dt);
        this.velocity.add(change);
        this.position.copy(safePosition(old, old.clone().addScaledVector(this.velocity, dt)));
        this.aim(distance < 140 ? new Vector3(...this.target.position) : waypoint, dt, 1.5);
      }
    } else {
      const direction = new Vector3(input.right, 0, -input.forward).applyQuaternion(this.rotation).addScaledVector(UP, input.up).clampLength(0, 1);
      let maximum = 18 * 2 ** this.speedLevel * (input.boost ? 5 : 1);
      maximum = Math.min(maximum, structureSpeedLimit(this.position, direction));
      for (const solid of SOLIDS) {
        const outward = this.position.clone().sub(new Vector3(...solid.position));
        const altitude = outward.length() - solid.radius;
        // Slow approaches and sideways passes, but let the pilot fly away from a surface.
        if (direction.dot(outward.normalize()) < .35) maximum = Math.min(maximum, Math.max(6, altitude * 1.4));
      }
      const desired = direction.multiplyScalar(input.brake ? 0 : maximum);
      const slowing = desired.lengthSq() < this.velocity.lengthSq() || desired.dot(this.velocity) < 0;
      this.velocity.lerp(desired, 1 - Math.exp(-(input.brake ? 24 : slowing ? 10 : 6) * dt));
      if (desired.lengthSq() === 0 && this.speed < .05) this.velocity.set(0, 0, 0);
      const proposed = old.clone().addScaledVector(this.velocity, dt);
      this.position.copy(safePosition(old, proposed));
      if (this.position.distanceToSquared(proposed) > .0001) this.velocity.multiplyScalar(.1);
    }
    this.traveled += old.distanceTo(this.position);
  }
}
